import { Box, Button, Container, Grid, Hidden, Typography } from '@mui/material';
import { ReactComponent as ClockWiseIcon } from 'assets/icons/clockwise-arrow.svg';
import { ReactComponent as CounterClockWiseIcon } from 'assets/icons/counter-clockwise-arrow.svg';
import { main_toolkit_background } from 'assets/images';
import Image from 'components/atoms/Image';
import { Layout } from 'components/organisms';
import {
	getDynamicFontSize,
	getGridSize,
	getIconScale,
	getImageSize,
	getObjectiveImageSize,
	getPhrasesFontSize,
	useGetToolkitData,
} from 'modules/toolkit';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { colors } from 'styles';

interface IntroducePageProps {}
type UserMenuProp = {
	title: string;
	description: string;
	image_url?: string;
};

type PhraseProp = {
	content: string;
	color: string;
	scale: number;
	z_index?: number;
};

const ToolkitPage: React.FC<IntroducePageProps> = ({}) => {
	const { data } = useGetToolkitData();

	return (
		<Layout>
			{/* Main start */}
			<Container
				maxWidth="xl"
				sx={{
					textAlign: 'center',
					p: '0px',
					background: 'linear-gradient(112.33deg, #EFF8FF -2.8%, #FFFFFF 61.03%, #D1E9FF 124.92%)',
					padding: '0px!important',
				}}
			>
				<Grid container textAlign="center" justifyContent="center">
					<Grid item xs={9} md={9} sm={9}>
						<Typography
							variant="h3"
							fontWeight="bold"
							lineHeight="60px"
							fontSize="48px"
							sx={{
								marginTop: {
									md: '100px',
								},
							}}
							dangerouslySetInnerHTML={{ __html: data?.hero_banner_toolkit?.headline || '' }}
						></Typography>
					</Grid>
					<Box
						sx={{
							backgroundImage: `url(${main_toolkit_background})`,
							width: '100%',
							backgroundRepeat: 'no-repeat',
							objectFit: 'cover',
							backgroundSize: 'cover',
						}}
					>
						<Box maxWidth="md" component="div" m="50px auto 0">
							<Image
								src={data?.hero_banner_toolkit?.image}
								style={{
									borderTopLeftRadius: '15px',
									borderTopRightRadius: '15px',
									width: '100%',
									padding: '18px 15px 0 15px',
									background: 'black',
								}}
								alt="demo"
								height="inherit"
							/>
						</Box>
					</Box>
				</Grid>
			</Container>
			{/* Main End */}

			{/* How to use Start */}
			<Container
				maxWidth="md"
				sx={{
					textAlign: 'center',
					mt: '70px',
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Inter',
						fontWeight: '600',
						fontSize: '16px',
						lineHeight: '24px',
						color: '#2E90FA',
					}}
					mb="12px"
				>
					{data?.how_to_use_section_toolkit?.name}
				</Typography>
				<Typography
					variant="h4"
					fontWeight={600}
					fontSize="36px"
					mb="24px"
					sx={{
						lineHeight: {
							md: '40px',
							sm: '40px',
							xs: '30px',
						},
						fontFamily: 'Inter',
					}}
					dangerouslySetInnerHTML={{ __html: data?.how_to_use_section_toolkit?.headline || '' }}
				></Typography>
				<Typography
					variant="subtitle1"
					sx={{
						lineHeight: {
							md: '30px',
							sm: '30px',
							xs: '20px',
						},
						color: colors.gray[500],
					}}
					dangerouslySetInnerHTML={{ __html: data?.how_to_use_section_toolkit?.body || '' }}
				></Typography>
			</Container>
			<Container
				maxWidth="lg"
				sx={{
					textAlign: 'center',
					mt: '70px',
				}}
			>
				<Grid spacing={4} container textAlign="center" justifyContent="center">
					{data?.how_to_use_section_toolkit?.steps &&
						data.how_to_use_section_toolkit.steps.map((item: UserMenuProp, i: number) => {
							const stepsCount = data.how_to_use_section_toolkit.steps.length;
							const gridSize = getGridSize(stepsCount);
							const fontSize = getDynamicFontSize(stepsCount);
							const imageSize = getImageSize(stepsCount);
							return (
								<Grid item xs={gridSize.xs} md={gridSize.md} sm={gridSize.sm}>
									<img style={imageSize} src={item.image_url || ''} alt="logo" />
									<Box>
										<Typography
											variant="subtitle1"
											fontWeight="600"
											sx={{
												lineHeight: {
													md: '24px',
													sm: '24px',
													xs: '20px',
												},
												height: '50px',
												fontFamily: 'Inter',
												fontSize: fontSize.title,
												color: '#000000',
											}}
										>
											{i + 1}. {item.title}
										</Typography>
										<Typography
											variant="subtitle1"
											fontWeight="400"
											sx={{
												lineHeight: {
													md: '20px',
													sm: '20px',
													xs: '18px',
												},
												fontFamily: 'Inter',
												fontSize: fontSize.description,
												mt: '20px',
												color: '#9DA4AE',
											}}
											dangerouslySetInnerHTML={{ __html: item.description || '' }}
										/>
									</Box>
								</Grid>
							);
						})}
				</Grid>
				<Link to="/categories" state={{ tab: 0 }} style={{ width: '100%' }}>
					<Button
						variant="contained"
						sx={{
							my: '70px',
							px: '2.5rem',
							width: {
								xs: '100%',
								md: '200px',
							},
							padding: {
								xs: '12px 20px',
								md: '6px 16px',
							},
						}}
					>
						{data?.how_to_use_section_toolkit?.button}
					</Button>
				</Link>
			</Container>
			{/* How to use End */}

			<Container maxWidth="xl" sx={{ padding: '0px!important' }}>
				{/* Objective start */}
				<Container
					maxWidth="xl"
					sx={{
						background: '#F9FAFB',
					}}
				>
					<Container
						maxWidth="lg"
						sx={{
							textAlign: 'center',
							py: '100px',
							pl: `0 !important`,
							pr: '0 !important',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Inter',
								fontWeight: '600',
								fontSize: '16px',
								lineHeight: '24px',
								color: '#2E90FA',
							}}
							mb="12px"
						>
							{data?.objectives_section_toolkit?.name}
						</Typography>
						<Typography
							variant="h4"
							fontWeight={600}
							fontSize="24px"
							mb="24px"
							mt="5px"
							sx={{
								lineHeight: {
									md: '32px',
									sm: '32px',
									xs: '26px',
								},
								fontFamily: 'Inter',
							}}
							dangerouslySetInnerHTML={{ __html: data?.objectives_section_toolkit?.headline || '' }}
						/>
						<Grid mt="30px" spacing={6} container textAlign="center" justifyContent="center">
							{data?.objectives_section_toolkit?.objectives &&
								data.objectives_section_toolkit.objectives.map((item: UserMenuProp) => {
									const objectivesCount = data.objectives_section_toolkit.objectives.length;
									const gridSize = getGridSize(objectivesCount);
									const fontSize = getDynamicFontSize(objectivesCount);
									const imageSize = getObjectiveImageSize(objectivesCount);
									return (
										<Grid item xs={gridSize.xs} md={gridSize.md} sm={gridSize.sm}>
											<img style={imageSize} src={item.image_url || ''} alt="logo" />
											<Typography
												variant="subtitle1"
												fontWeight="600"
												sx={{
													lineHeight: {
														md: '24px',
														sm: '24px',
														xs: '20px',
													},
													fontFamily: 'Inter',
													fontSize: fontSize.title,
													mt: '10px',
													height: '50px',
													color: '#384250',
												}}
											>
												{item.title}
											</Typography>
											<Typography
												mt="10px"
												variant="subtitle1"
												sx={{
													lineHeight: {
														md: '30px',
														sm: '30px',
														xs: '20px',
													},
													color: colors.gray[500],
													fontFamily: 'Inter',
													fontSize: fontSize.description,
												}}
												dangerouslySetInnerHTML={{ __html: item?.description || '' }}
											/>
										</Grid>
									);
								})}
						</Grid>
					</Container>
				</Container>
				{/* Objective End */}
				{/* Overall Start */}
				<Container sx={{ padding: '50px 0px 0px 0px !important', mt: '10px' }} maxWidth="xl">
					<Container
						maxWidth="md"
						sx={{
							textAlign: 'center',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Inter',
								fontWeight: '600',
								fontSize: '16px',
								lineHeight: '24px',
								color: '#2E90FA',
							}}
							mb="12px"
						>
							{data?.where_does_the_toolkit_fit_section_toolkit?.name}
						</Typography>
						<Typography
							variant="h4"
							fontWeight={600}
							fontSize="40px"
							mb="24px"
							mt="5px"
							sx={{
								lineHeight: {
									md: '48px',
									xs: '40px',
								},
								fontFamily: 'Inter',
								color: '#111927',
							}}
							dangerouslySetInnerHTML={{ __html: data?.where_does_the_toolkit_fit_section_toolkit?.headline || '' }}
						/>
						<Typography
							variant="subtitle1"
							fontSize="16px"
							sx={{
								lineHeight: {
									md: '30px',
									sm: '30px',
									xs: '20px',
								},
								color: colors.gray[500],
								fontFamily: 'Inter',
							}}
							dangerouslySetInnerHTML={{ __html: data?.where_does_the_toolkit_fit_section_toolkit?.body || '' }}
						/>
					</Container>
					{/* Overall End */}
					<Box
						maxWidth="lg"
						component="div"
						m="20px auto"
						pt={{ xs: '40px', md: '100px' }}
						pb={{ xs: '60px', md: '200px' }}
						display="flex"
						alignItems="center"
						flexDirection={{ xs: 'column', md: 'row' }}
					>
						{data?.where_does_the_toolkit_fit_section_toolkit?.phrases.map(
							(it: PhraseProp, id, array: PhraseProp[]) => {
								const phrasesCount = array.length;
								const dynamicWidth = `calc(100% / ${phrasesCount})`;
								const fontSize = getPhrasesFontSize(phrasesCount);
								const iconScale = getIconScale(phrasesCount);
								return (
									<Box
										key={id}
										display="flex"
										flexDirection="column"
										justifyContent="center"
										alignItems="center"
										borderRadius="50%"
										position="relative"
										sx={{
											backgroundColor: it.color,
											width: { xs: '250px', md: dynamicWidth },
											paddingBottom: { xs: '250px', md: dynamicWidth },
											height: 0,
											position: 'relative',
											transform: {
												xs: `translateY(-${id !== 0 ? (30 * id) / (it.scale * 2) : 0}px) scale(${it.scale})`,
												md: `scale(${it.scale})`, // only keep scale on md
											},
											marginTop: {
												xs: 0,
												md: id % 2 ? '110px' : '0px',
											},
											zIndex: it.z_index,
											flexShrink: 0,
										}}
									>
										{!(id % 2) && id < array.length - 1 && (
											<Hidden mdDown>
												<Box
													position="absolute"
													top={`${6 / array[id + 1].scale}%`}
													right={`-${34 / array[id + 1].scale}%`}
													sx={{ transform: `scale(${iconScale})` }}
												>
													<ClockWiseIcon />
												</Box>
											</Hidden>
										)}
										{!!(id % 2) && id < array.length - 1 && (
											<Hidden mdDown>
												<Box
													position="absolute"
													bottom={`${6 / array[id + 1].scale}%`}
													right={`-${26 / array[id + 1].scale}%`}
													sx={{
														transform: `scale(${iconScale})`,
														marginLeft: '10px',
													}}
												>
													<CounterClockWiseIcon />
												</Box>
											</Hidden>
										)}
										<Typography
											fontSize={fontSize}
											fontWeight={400}
											textAlign="center"
											sx={{
												position: 'absolute',
												top: '50%',
												left: '50%',
												width: '67%',
												transform: 'translate(-50%, -50%)',
											}}
										>
											{it.content}
										</Typography>
									</Box>
								);
							}
						)}
					</Box>
				</Container>
				{/* Objective End */}
			</Container>
		</Layout>
	);
};

export default ToolkitPage;
